import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload, Trash2, ArrowLeft, Image as ImageIcon, FileText, Edit2, X, Eye, EyeOff, Plus, LogOut, Clock, Save } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import './AdminPage.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DAYS_OF_WEEK = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];

export default function AdminPage() {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('fotos');
  const [photos, setPhotos] = useState([]);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState('');

  // Promociones state
  const [promociones, setPromociones] = useState([]);
  const [promoForm, setPromoForm] = useState({ title: '', content: '', image_url: '', is_active: true });
  const [editingPromo, setEditingPromo] = useState(null);
  const [promoImageFile, setPromoImageFile] = useState(null);
  const [promoImagePreview, setPromoImagePreview] = useState(null);
  const [promoUploading, setPromoUploading] = useState(false);

  // Horarios state
  const [horarios, setHorarios] = useState([]);
  const [notaEspecial, setNotaEspecial] = useState('');
  const [savingHorarios, setSavingHorarios] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const fetchPhotos = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/photos/all`, { credentials: 'include' });
      const data = await res.json();
      setPhotos(data.photos || []);
    } catch (err) {
      console.error('Error fetching photos:', err);
    }
  }, []);

  const fetchPromociones = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/promociones?active_only=false`, { credentials: 'include' });
      const data = await res.json();
      setPromociones(data.promociones || []);
    } catch (err) {
      console.error('Error fetching promociones:', err);
    }
  }, []);

  const fetchHorarios = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/horarios`, { credentials: 'include' });
      const data = await res.json();
      setHorarios(data.horarios || []);
      setNotaEspecial(data.nota_especial || '');
    } catch (err) {
      console.error('Error fetching horarios:', err);
    }
  }, []);

  useEffect(() => {
    fetchPhotos();
    fetchPromociones();
    fetchHorarios();
  }, [fetchPhotos, fetchPromociones, fetchHorarios]);

  const handleFileChange = (e) => {
    const selected = e.target.files[0];
    if (selected) {
      setFile(selected);
      setPreview(URL.createObjectURL(selected));
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const dropped = e.dataTransfer.files[0];
    if (dropped) {
      setFile(dropped);
      setPreview(URL.createObjectURL(dropped));
    }
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    setMessage('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('title', title || 'Sin título');
      formData.append('description', description || '');

      const res = await fetch(`${API_URL}/api/photos/upload?title=${encodeURIComponent(title || 'Sin título')}&description=${encodeURIComponent(description || '')}`, {
        method: 'POST',
        body: formData,
      });

      if (res.ok) {
        setMessage('Foto subida exitosamente');
        setFile(null);
        setPreview(null);
        setTitle('');
        setDescription('');
        fetchPhotos();
      } else {
        setMessage('Error al subir la foto');
      }
    } catch (err) {
      setMessage('Error de conexión');
    }
    setUploading(false);
  };

  const handleDelete = async (url) => {
    if (!window.confirm('¿Eliminar esta foto?')) return;
    try {
      const res = await fetch(`${API_URL}/api/photos?url=${encodeURIComponent(url)}`, {
        method: 'DELETE',
      });
      if (res.ok) {
        fetchPhotos();
        setMessage('Foto eliminada');
      }
    } catch (err) {
      setMessage('Error al eliminar');
    }
  };

  // Promociones handlers
  const handlePromoImageChange = (e) => {
    const selected = e.target.files[0];
    if (selected) {
      setPromoImageFile(selected);
      setPromoImagePreview(URL.createObjectURL(selected));
    }
  };

  const uploadPromoImage = async () => {
    if (!promoImageFile) return promoForm.image_url;
    
    const formData = new FormData();
    formData.append('file', promoImageFile);
    
    const res = await fetch(`${API_URL}/api/promociones/upload-image`, {
      method: 'POST',
      body: formData,
    });
    
    if (res.ok) {
      const data = await res.json();
      return data.image_url;
    }
    throw new Error('Error al subir imagen');
  };

  const handlePromoSubmit = async (e) => {
    e.preventDefault();
    setPromoUploading(true);
    setMessage('');
    
    try {
      let imageUrl = promoForm.image_url;
      if (promoImageFile) {
        imageUrl = await uploadPromoImage();
      }
      
      const promoData = {
        ...promoForm,
        image_url: imageUrl,
      };
      
      if (editingPromo) {
        // Update
        const res = await fetch(`${API_URL}/api/promociones/${editingPromo.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(promoData),
        });
        if (res.ok) {
          setMessage('Promoción actualizada');
        }
      } else {
        // Create
        const res = await fetch(`${API_URL}/api/promociones`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(promoData),
        });
        if (res.ok) {
          setMessage('Promoción creada');
        }
      }
      
      // Reset form
      setPromoForm({ title: '', content: '', image_url: '', is_active: true });
      setEditingPromo(null);
      setPromoImageFile(null);
      setPromoImagePreview(null);
      fetchPromociones();
    } catch (err) {
      setMessage('Error al guardar promoción');
    }
    setPromoUploading(false);
  };

  const handleEditPromo = (promo) => {
    setEditingPromo(promo);
    setPromoForm({
      title: promo.title,
      content: promo.content,
      image_url: promo.image_url,
      is_active: promo.is_active,
    });
    setPromoImagePreview(promo.image_url);
    setPromoImageFile(null);
  };

  const handleDeletePromo = async (id) => {
    if (!window.confirm('¿Eliminar esta promoción?')) return;
    try {
      const res = await fetch(`${API_URL}/api/promociones/${id}`, {
        method: 'DELETE',
      });
      if (res.ok) {
        fetchPromociones();
        setMessage('Promoción eliminada');
      }
    } catch (err) {
      setMessage('Error al eliminar');
    }
  };

  const togglePromoActive = async (promo) => {
    try {
      await fetch(`${API_URL}/api/promociones/${promo.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_active: !promo.is_active }),
      });
      fetchPromociones();
    } catch (err) {
      setMessage('Error al actualizar');
    }
  };

  const cancelEditPromo = () => {
    setEditingPromo(null);
    setPromoForm({ title: '', content: '', image_url: '', is_active: true });
    setPromoImageFile(null);
    setPromoImagePreview(null);
  };

  // Horarios handlers
  const updateHorario = (index, field, value) => {
    const newHorarios = [...horarios];
    newHorarios[index] = { ...newHorarios[index], [field]: value };
    setHorarios(newHorarios);
  };

  const handleSaveHorarios = async () => {
    setSavingHorarios(true);
    setMessage('');
    try {
      const res = await fetch(`${API_URL}/api/horarios`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          horarios: horarios,
          nota_especial: notaEspecial,
        }),
      });
      if (res.ok) {
        setMessage('Horarios guardados');
        fetchHorarios();
      } else {
        setMessage('Error al guardar horarios');
      }
    } catch (err) {
      setMessage('Error al guardar horarios');
    }
    setSavingHorarios(false);
  };

  return (
    <div className="admin-page" data-testid="admin-page">
      <div className="admin-container">
        <header className="admin-header">
          <div className="admin-header-top">
            <a href="/" className="admin-back" data-testid="admin-back-link">
              <ArrowLeft size={20} />
              Volver al sitio
            </a>
            <button onClick={handleLogout} className="admin-logout-btn" data-testid="logout-btn">
              <LogOut size={18} />
              Cerrar sesión
            </button>
          </div>
          <h1>Panel de Administración</h1>
          <p>Gestiona el contenido de Dogos El Tío Chepe {admin && <span className="admin-user">({admin.email})</span>}</p>
        </header>

        {/* Tabs */}
        <div className="admin-tabs" data-testid="admin-tabs">
          <button 
            className={`admin-tab ${activeTab === 'fotos' ? 'active' : ''}`}
            onClick={() => setActiveTab('fotos')}
            data-testid="tab-fotos"
          >
            <ImageIcon size={18} /> Fotos
          </button>
          <button 
            className={`admin-tab ${activeTab === 'promociones' ? 'active' : ''}`}
            onClick={() => setActiveTab('promociones')}
            data-testid="tab-promociones"
          >
            <FileText size={18} /> Promociones
          </button>
          <button 
            className={`admin-tab ${activeTab === 'horarios' ? 'active' : ''}`}
            onClick={() => setActiveTab('horarios')}
            data-testid="tab-horarios"
          >
            <Clock size={18} /> Horarios
          </button>
        </div>

        {message && <p className="admin-message" data-testid="admin-message">{message}</p>}

        {/* Fotos Tab */}
        {activeTab === 'fotos' && (
          <>
            <section className="admin-upload-section" data-testid="upload-section">
              <h2><Upload size={22} /> Subir Nueva Foto</h2>
              <div
                className={`admin-dropzone ${preview ? 'has-preview' : ''}`}
                onDragOver={(e) => e.preventDefault()}
                onDrop={handleDrop}
                data-testid="upload-dropzone"
              >
                {preview ? (
                  <img src={preview} alt="Preview" className="admin-preview" />
                ) : (
                  <div className="admin-dropzone-content">
                    <ImageIcon size={48} strokeWidth={1} />
                    <p>Arrastra una imagen aquí o haz clic para seleccionar</p>
                  </div>
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="admin-file-input"
                  data-testid="upload-file-input"
                />
              </div>
              <div className="admin-form-row">
                <input
                  type="text"
                  placeholder="Título de la foto"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="admin-input"
                  data-testid="upload-title-input"
                />
                <input
                  type="text"
                  placeholder="Descripción (opcional)"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="admin-input"
                  data-testid="upload-description-input"
                />
              </div>
              <button
                onClick={handleUpload}
                disabled={!file || uploading}
                className="admin-upload-btn"
                data-testid="upload-submit-btn"
              >
                {uploading ? 'Subiendo...' : 'Subir Foto'}
              </button>
            </section>

            <section className="admin-gallery-section" data-testid="admin-gallery">
              <h2>Fotos Actuales ({photos.length})</h2>
              <p className="admin-gallery-note">Las 3 fotos más recientes se muestran en la página principal.</p>
              {photos.length === 0 ? (
                <p className="admin-empty">No hay fotos aún</p>
              ) : (
                <div className="admin-photo-grid">
                  {photos.map((photo, i) => (
                    <div key={i} className={`admin-photo-card ${i < 3 ? 'visible-on-site' : ''}`} data-testid={`admin-photo-${i}`}>
                      <div className="admin-photo-img-wrap">
                        <img src={photo.url} alt={photo.title} />
                        {i < 3 && <span className="admin-badge">Visible</span>}
                      </div>
                      <div className="admin-photo-info">
                        <strong>{photo.title}</strong>
                        <small>{photo.description}</small>
                      </div>
                      <button onClick={() => handleDelete(photo.url)} className="admin-delete-btn" data-testid={`delete-photo-${i}`}>
                        <Trash2 size={16} /> Eliminar
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </>
        )}

        {/* Promociones Tab */}
        {activeTab === 'promociones' && (
          <>
            <section className="admin-upload-section" data-testid="promo-form-section">
              <h2>
                {editingPromo ? <><Edit2 size={22} /> Editar Promoción</> : <><Plus size={22} /> Nueva Promoción</>}
              </h2>
              <form onSubmit={handlePromoSubmit} className="admin-promo-form">
                <div className="admin-promo-image-section">
                  <div
                    className={`admin-dropzone ${promoImagePreview ? 'has-preview' : ''}`}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                      e.preventDefault();
                      const dropped = e.dataTransfer.files[0];
                      if (dropped) {
                        setPromoImageFile(dropped);
                        setPromoImagePreview(URL.createObjectURL(dropped));
                      }
                    }}
                  >
                    {promoImagePreview ? (
                      <img src={promoImagePreview} alt="Preview" className="admin-preview" />
                    ) : (
                      <div className="admin-dropzone-content">
                        <ImageIcon size={48} strokeWidth={1} />
                        <p>Imagen de la promoción</p>
                      </div>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handlePromoImageChange}
                      className="admin-file-input"
                      data-testid="promo-image-input"
                    />
                  </div>
                  <span className="admin-or">o</span>
                  <input
                    type="url"
                    placeholder="URL de imagen externa"
                    value={promoForm.image_url}
                    onChange={(e) => {
                      setPromoForm({ ...promoForm, image_url: e.target.value });
                      if (!promoImageFile) setPromoImagePreview(e.target.value);
                    }}
                    className="admin-input"
                    data-testid="promo-image-url-input"
                  />
                </div>
                
                <input
                  type="text"
                  placeholder="Título de la promoción *"
                  value={promoForm.title}
                  onChange={(e) => setPromoForm({ ...promoForm, title: e.target.value })}
                  className="admin-input"
                  required
                  data-testid="promo-title-input"
                />
                
                <textarea
                  placeholder="Contenido de la promoción *"
                  value={promoForm.content}
                  onChange={(e) => setPromoForm({ ...promoForm, content: e.target.value })}
                  className="admin-textarea"
                  rows={8}
                  required
                  data-testid="promo-content-input"
                />
                
                <label className="admin-checkbox">
                  <input
                    type="checkbox"
                    checked={promoForm.is_active}
                    onChange={(e) => setPromoForm({ ...promoForm, is_active: e.target.checked })}
                    data-testid="promo-active-checkbox"
                  />
                  <span>Publicar promoción (visible en el sitio)</span>
                </label>
                
                <div className="admin-form-actions">
                  {editingPromo && (
                    <button type="button" onClick={cancelEditPromo} className="admin-cancel-btn" data-testid="promo-cancel-btn">
                      <X size={16} /> Cancelar
                    </button>
                  )}
                  <button
                    type="submit"
                    disabled={promoUploading || !promoForm.title || !promoForm.content || (!promoForm.image_url && !promoImageFile)}
                    className="admin-upload-btn"
                    data-testid="promo-submit-btn"
                  >
                    {promoUploading ? 'Guardando...' : editingPromo ? 'Actualizar' : 'Crear Promoción'}
                  </button>
                </div>
              </form>
            </section>

            <section className="admin-gallery-section" data-testid="promo-list-section">
              <h2>Promociones ({promociones.length})</h2>
              {promociones.length === 0 ? (
                <p className="admin-empty">No hay promociones aún</p>
              ) : (
                <div className="admin-promo-list">
                  {promociones.map((promo) => (
                    <div key={promo.id} className={`admin-promo-card ${!promo.is_active ? 'inactive' : ''}`} data-testid={`promo-item-${promo.id}`}>
                      <div className="admin-promo-thumb">
                        <img src={promo.image_url} alt={promo.title} />
                      </div>
                      <div className="admin-promo-info">
                        <h3>{promo.title}</h3>
                        <p>{promo.content.substring(0, 100)}...</p>
                        <span className={`admin-promo-status ${promo.is_active ? 'active' : 'inactive'}`}>
                          {promo.is_active ? 'Publicada' : 'Oculta'}
                        </span>
                      </div>
                      <div className="admin-promo-actions">
                        <button onClick={() => togglePromoActive(promo)} className="admin-icon-btn" title={promo.is_active ? 'Ocultar' : 'Publicar'} data-testid={`promo-toggle-${promo.id}`}>
                          {promo.is_active ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                        <button onClick={() => handleEditPromo(promo)} className="admin-icon-btn" title="Editar" data-testid={`promo-edit-${promo.id}`}>
                          <Edit2 size={18} />
                        </button>
                        <button onClick={() => handleDeletePromo(promo.id)} className="admin-icon-btn delete" title="Eliminar" data-testid={`promo-delete-${promo.id}`}>
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </>
        )}

        {/* Horarios Tab */}
        {activeTab === 'horarios' && (
          <section className="admin-upload-section" data-testid="horarios-section">
            <h2><Clock size={22} /> Horarios de Atención</h2>
            <p className="admin-section-desc">Configura los días y horarios de apertura del restaurante</p>
            
            <div className="admin-horarios-grid">
              {horarios.map((horario, index) => (
                <div key={horario.day} className={`admin-horario-row ${!horario.is_open ? 'closed' : ''}`} data-testid={`horario-row-${index}`}>
                  <div className="admin-horario-day">
                    <label className="admin-horario-toggle">
                      <input
                        type="checkbox"
                        checked={horario.is_open}
                        onChange={(e) => {
                          updateHorario(index, 'is_open', e.target.checked);
                          if (!e.target.checked) {
                            updateHorario(index, 'hours', 'Cerrado');
                          }
                        }}
                        data-testid={`horario-toggle-${index}`}
                      />
                      <span className="admin-horario-slider"></span>
                    </label>
                    <span className="admin-horario-day-name">{horario.day}</span>
                  </div>
                  <div className="admin-horario-hours">
                    {horario.is_open ? (
                      <input
                        type="text"
                        value={horario.hours}
                        onChange={(e) => updateHorario(index, 'hours', e.target.value)}
                        placeholder="Ej: 4:00 PM — 11:00 PM"
                        className="admin-input"
                        data-testid={`horario-hours-${index}`}
                      />
                    ) : (
                      <span className="admin-horario-closed">Cerrado</span>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="admin-horario-nota">
              <label>Nota especial (opcional)</label>
              <textarea
                value={notaEspecial}
                onChange={(e) => setNotaEspecial(e.target.value)}
                placeholder="Ej: Cuando el lunes es feriado, el domingo el horario es de 11:00 AM a 11:00 PM"
                className="admin-textarea"
                rows={3}
                data-testid="horario-nota-input"
              />
            </div>

            <button
              onClick={handleSaveHorarios}
              disabled={savingHorarios}
              className="admin-upload-btn admin-save-btn"
              data-testid="horarios-save-btn"
            >
              <Save size={18} />
              {savingHorarios ? 'Guardando...' : 'Guardar Horarios'}
            </button>
          </section>
        )}
      </div>
    </div>
  );
}
